

import Footer from "../../../components/es/FooterEs";
import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { Link } from 'react-router-dom';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgVideo from 'lightgallery/plugins/video';
import lgAutoplay from 'lightgallery/plugins/autoplay';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';

import WOW from 'wowjs';

import Adx1BannerVideo from '../../../components/Adx1BannerVideo';

function Video() {
    const galleryRef = useRef(null);
    const wowRef = useRef(null);
    const [availableVideos, setAvailableVideos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Memoizar la detección de dispositivos táctiles
    const isTouchDevice = useMemo(() => 
        'ontouchstart' in window || navigator.maxTouchPoints > 0, []);
    
    const isIOS = useMemo(() => 
        /iPad|iPhone|iPod/.test(navigator.userAgent), []);

    // Función para verificar si un archivo existe
    const checkFileExists = useCallback((url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
    }, []);

    // Memoizar constantes
            const startRange = useMemo(() => 784, []);
            const endRange = useMemo(() => 851, []);
        

    // Efecto para cargar solo los videos disponibles
    useEffect(() => {
        const loadAvailableVideos = async () => {
            setIsLoading(true);
            const videoPromises = [];
            
            for (let i = startRange; i <= endRange; i++) {
                const videoSrc = `/assets/video/open/03-09-25/VID_${i}.mp4`;
                const videoPoster = `/assets/video/open/03-09-25/covers/IMG_${i}.webp`;
                const videoDescription = `VIDEO_${i} | Puede obtener el video en alta calidad con una donación para nuestro proyecto, escríbranos por telegram`;

                // Verificar si el poster existe (usamos el poster como referencia de existencia)
                videoPromises.push(
                    checkFileExists(videoPoster).then(exists => ({
                        exists,
                        data: {
                            src: videoSrc,
                            poster: videoPoster,
                            description: videoDescription,
                            index: i
                        }
                    }))
                );
            }

            try {
                const results = await Promise.all(videoPromises);
                const existingVideos = results
                    .filter(result => result.exists)
                    .map(result => result.data);
                
                setAvailableVideos(existingVideos);
            } catch (error) {
                console.error('Error checking video files:', error);
                // En caso de error, fallback a la lista completa
                const fallbackVideos = [];
                for (let i = startRange; i <= endRange; i++) {
                    const videoSrc = `/assets/video/open/03-09-25/VID_${i}.mp4`;
                    const videoPoster = `/assets/video/open/03-09-25/covers/IMG_${i}.webp`;
                    const videoDescription = `VIDEO_${i} | Puede obtener el video en alta calidad con una donación para nuestro proyecto, escríbranos por telegram`;

                    fallbackVideos.push({
                        src: videoSrc,
                        poster: videoPoster,
                        description: videoDescription,
                        index: i
                    });
                }
                setAvailableVideos(fallbackVideos);
            } finally {
                setIsLoading(false);
            }
        };

        loadAvailableVideos();
    }, [startRange, endRange, checkFileExists]);

    // Optimizar generateSubHtml con useCallback
    const generateSubHtml = useCallback((video) => {
        const downloadSrc = video.src;
        const description = video.description;
        const downloadButton = isIOS ? (
            <a href={downloadSrc} target="_blank" className="download-btn">Descargar</a>
        ) : (
            <a href={downloadSrc} download className="download-btn">Descargar</a>
        );

        const additionalButton = (
            <a href="https://t.me/latinoxclub" className="donate-btn">CONTACTAR</a>
        );

        const html = (
            <div>
                <div style={{ justifyContent: 'space-between' }}>
                    {downloadButton}
                    {additionalButton}
                </div>
                <h4>{description}</h4>
            </div>
        );
        return renderToString(html);
    }, [isIOS]);

    // Optimizar handleVideoOpen con useCallback
    const handleVideoOpen = useCallback((videoSrc) => {
        const blockId = isTouchDevice ? "R-A-9762127-6" : "R-A-9762127-7";
        const platform = isTouchDevice ? "touch" : "desktop";

        window.yaContextCb.push(() => {
            // eslint-disable-next-line no-undef
            Ya.Context.AdvManager.render({
                blockId: blockId,
                type: "fullscreen",
                platform: platform,
                renderTo: 'Adx1FullScreen'
            });
        });

        console.log(`Abriendo video: ${videoSrc}`);
    }, [isTouchDevice]);

    // Inicializar WOW con cleanup
    useEffect(() => {
        if (!wowRef.current) {
            wowRef.current = new WOW.WOW();
            wowRef.current.init();
        }

        return () => {
            // Cleanup si es necesario
            if (wowRef.current) {
                wowRef.current = null;
            }
        };
    }, []);

    // Inicializar lightGallery cuando los videos estén cargados
    useEffect(() => {
        if (availableVideos.length === 0 || isLoading) return;

        const lightGalleryInit = () => {
            const gallery = document.getElementById('video-gallery');

            if (gallery && !galleryRef.current) {
                galleryRef.current = lightGallery(gallery, {
                    plugins: [lgVideo, lgThumbnail, lgFullscreen, lgAutoplay, lgZoom],
                    videojs: false,
                    autoplayFirstVideo: false,
                });
            }
        };

        const timer = setTimeout(lightGalleryInit, 100);

        return () => {
            clearTimeout(timer);
            // Cleanup lightGallery
            if (galleryRef.current) {
                galleryRef.current.destroy();
                galleryRef.current = null;
            }
        };
    }, [availableVideos, isLoading]);

    // Memoizar el contenido de los videos para evitar re-renders innecesarios
    const videoItems = useMemo(() => {
        if (isLoading) {
            return (
                <div className="col-12 text-center">
                    <p>Cargando videos disponibles...</p>
                </div>
            );
        }

        if (availableVideos.length === 0) {
            return (
                <div className="col-12 text-center">
                    <p>No hay videos disponibles en este momento.</p>
                </div>
            );
        }

        return availableVideos.map((video, index) => (
            <div 
                key={`video-${video.index}`}
                className="col-4 col-sm-3 col-md-3 col-lg-2 col-xl-2 item"
                onClick={() => handleVideoOpen(video.src)}
                data-video={JSON.stringify({ 
                    source: [{ src: video.src, type: "video/mp4" }], 
                    attributes: { preload: false, controls: true } 
                })}
                data-poster=""
                data-sub-html={generateSubHtml(video)}
            >
                <a>
                    <img
                        className="img-responsive"
                        src={video.poster}
                        alt={`Video ${video.index}`}
                        loading="lazy"
                    />
                </a>
            </div>
        ));
    }, [availableVideos, isLoading, handleVideoOpen, generateSubHtml]);

    return (
        <main>
            <section
                className="hero-wrap hero-wrap-2-fox"
                style={{ backgroundImage: 'url("/assets/video/open/03-09-25/covers/IMG_03-09-25.webp")' }}
            >
                <div className="overlay" />
                <div className="container">
                    <div className="row no-gutters slider-text align-items-center justify-content-center">
                        <div className="col-md-9 ftco-animate text-center fadeInUp ftco-animated">
                            <h1 className="mb-2 bread"> Open Air 03-09-25 </h1>
                            <p className="breadcrumbs">
                                <span className="mr-2">
                                    <Link to="/gallery">Galería<i className="ion-ios-arrow-forward" /></Link>
                                </span>{" "}
                                <span>
                                    <Link to="/video-events">Todos los Vídeos<i className="ion-ios-arrow-forward" /></Link>
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Inserta el contenedor para el anuncio de Yandex */}
            <div id="Adx1FullScreen"></div>

            {/* -------------- Yandex Banner 03 -------------- */}
            <Adx1BannerVideo blockId="R-A-9762127-3" renderTo="yandex_rtb_R-A-9762127-3" />

            <div className="site-wrap front-videos">
                <div className="container">
                    <div className="row" id="video-gallery">
                        {videoItems}
                    </div>
                </div>
            </div>

            {/* -------------- Yandex Banner 04 -------------- */}
            <Adx1BannerVideo blockId="R-A-9762127-4" renderTo="yandex_rtb_R-A-9762127-4" />

            <Footer />
        </main>
    );
}

export default Video;